import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import {
  FiZap, FiMessageSquare, FiMail, FiFilter, FiDownload,
  FiChevronLeft, FiChevronRight, FiBarChart2
} from 'react-icons/fi';
import api from '../api';

export default function PointsUsage() {
  const navigate = useNavigate();
  const [usage, setUsage] = useState([]);
  const [stats, setStats] = useState({
    totalCount: 0,
    totalFree: 0,
    totalPaid: 0,
    totalCost: 0
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });
  const [filter, setFilter] = useState({
    serviceType: '', // 'push', 'alimtalk', 'sms'
    month: format(new Date(), 'yyyy-MM'),
    startDate: '',
    endDate: ''
  });
  const [loading, setLoading] = useState(true);

  const loadUsage = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...(filter.serviceType && { serviceType: filter.serviceType }),
        ...(filter.month && { month: filter.month }),
        ...(filter.startDate && { startDate: filter.startDate }),
        ...(filter.endDate && { endDate: filter.endDate })
      };

      const res = await api.get('/api/points/usage', { params });
      setUsage(res.data.usage || []);
      setStats(res.data.stats || { totalCount: 0, totalFree: 0, totalPaid: 0, totalCost: 0 });
      setPagination(res.data.pagination);
    } catch (error) {
      console.error('Failed to load usage:', error);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.limit, filter]);

  useEffect(() => {
    loadUsage();
  }, [loadUsage]);

  const handleFilterChange = (field, value) => {
    setFilter(prev => ({ ...prev, [field]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const resetFilter = () => {
    setFilter({
      serviceType: '',
      month: format(new Date(), 'yyyy-MM'),
      startDate: '',
      endDate: ''
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const exportToExcel = () => {
    // TODO: 엑셀 다운로드 구현
    alert('엑셀 다운로드 기능은 준비 중입니다.');
  };

  const getServiceIcon = (type) => {
    switch (type) {
      case 'push':
        return FiZap;
      case 'alimtalk':
        return FiMessageSquare;
      case 'sms':
        return FiMail;
      default:
        return FiBarChart2;
    }
  };

  const getServiceName = (type) => {
    const names = {
      push: '푸시 알림',
      alimtalk: '알림톡',
      sms: 'SMS'
    };
    return names[type] || type;
  };

  const getServiceColor = (type) => {
    const colors = {
      push: '#3B82F6',
      alimtalk: '#10B981',
      sms: '#F59E0B'
    };
    return colors[type] || '#6B7280';
  };

  const renderUsageItem = (item) => {
    const Icon = getServiceIcon(item.serviceType);
    const color = getServiceColor(item.serviceType);

    return (
      <div key={item._id} className="usage-item">
        <div className="usage-icon" style={{ backgroundColor: `${color}20`, color }}>
          <Icon size={20} />
        </div>
        <div className="usage-content">
          <div className="usage-main">
            <span className="usage-service">{getServiceName(item.serviceType)}</span>
            <span className="usage-quantity">{item.quantity}건</span>
          </div>
          <div className="usage-meta">
            <span className="usage-date">
              {format(new Date(item.usedAt), 'yyyy-MM-dd HH:mm')}
            </span>
            <span className="usage-month">청구월: {item.billingMonth}</span>
          </div>
          {item.description && (
            <div className="usage-description">{item.description}</div>
          )}
        </div>
        <div className="usage-breakdown">
          {item.freeCount > 0 && (
            <div className="usage-row free">
              <span>무료</span>
              <span>{item.freeCount}건</span>
            </div>
          )}
          {item.paidCount > 0 && (
            <div className="usage-row paid">
              <span>유료</span>
              <span>{item.paidCount}건</span>
            </div>
          )}
          <div className="usage-row cost">
            <span>비용</span>
            <span className="cost-value">{item.usedAmount.toLocaleString()}원</span>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="points-usage-page">
      <div className="page-header">
        <div>
          <button className="btn-back" onClick={() => navigate('/points')}>
            <FiChevronLeft />
          </button>
          <div>
            <h2>사용 내역 상세</h2>
            <p>서비스별 사용 내역을 확인하세요</p>
          </div>
        </div>
        <div className="header-actions">
          <button className="btn-secondary" onClick={exportToExcel}>
            <FiDownload />
            엑셀 다운로드
          </button>
        </div>
      </div>

      {/* 통계 요약 */}
      <div className="stats-summary">
        <div className="stat-box">
          <span className="stat-label">총 발송</span>
          <span className="stat-value">{stats.totalCount.toLocaleString()}건</span>
        </div>
        <div className="stat-box free">
          <span className="stat-label">무료 발송</span>
          <span className="stat-value">{stats.totalFree.toLocaleString()}건</span>
        </div>
        <div className="stat-box paid">
          <span className="stat-label">유료 발송</span>
          <span className="stat-value">{stats.totalPaid.toLocaleString()}건</span>
        </div>
        <div className="stat-box cost">
          <span className="stat-label">총 비용</span>
          <span className="stat-value">{stats.totalCost.toLocaleString()}원</span>
        </div>
      </div>

      {/* 필터 */}
      <div className="filter-section">
        <div className="filter-group">
          <label>서비스 유형</label>
          <select
            value={filter.serviceType}
            onChange={(e) => handleFilterChange('serviceType', e.target.value)}
          >
            <option value="">전체</option>
            <option value="push">푸시 알림</option>
            <option value="alimtalk">알림톡</option>
            <option value="sms">SMS</option>
          </select>
        </div>

        <div className="filter-group">
          <label>청구월</label>
          <input
            type="month"
            value={filter.month}
            onChange={(e) => handleFilterChange('month', e.target.value)}
          />
        </div>

        <div className="filter-group">
          <label>시작일</label>
          <input
            type="date"
            value={filter.startDate}
            onChange={(e) => handleFilterChange('startDate', e.target.value)}
          />
        </div>

        <div className="filter-group">
          <label>종료일</label>
          <input
            type="date"
            value={filter.endDate}
            onChange={(e) => handleFilterChange('endDate', e.target.value)}
          />
        </div>

        <button className="btn-reset" onClick={resetFilter}>
          <FiFilter />
          필터 초기화
        </button>
      </div>

      {/* 사용 내역 목록 */}
      <div className="usage-list">
        {loading ? (
          <div className="loading">로딩 중...</div>
        ) : usage.length === 0 ? (
          <div className="empty-state">
            <p>사용 내역이 없습니다.</p>
          </div>
        ) : (
          <>
            {usage.map(renderUsageItem)}

            {/* 페이지네이션 */}
            {pagination.pages > 1 && (
              <div className="pagination">
                <button
                  className="btn-page"
                  disabled={pagination.page === 1}
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                >
                  <FiChevronLeft />
                  이전
                </button>

                <span className="page-info">
                  {pagination.page} / {pagination.pages} 페이지
                </span>

                <button
                  className="btn-page"
                  disabled={pagination.page === pagination.pages}
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                >
                  다음
                  <FiChevronRight />
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
